import { z } from "zod";
import { UuidSchema } from "./common.js";
import { UserRoleSchema } from "./users.js";

// Contrato único do Assistente de IA: REST + SSE (superfície HTTP da plataforma) e
// engine (plataforma↔n8n). Fonte de tipos via z.infer (routes/services não importam zod).
// Catálogo de comandos vive em assistant-catalog.ts; canais/filas em ../queue.

export const ASSISTANT_PROTOCOL_VERSION = "1";

// Índice fino do catálogo por papel, injetado no request do turno. Tipos/Zod moram
// aqui (leaf) p/ o request referenciá-los sem ciclo com assistant-catalog-manifest.ts (que os produz
// via `buildCatalogIndex`). SEM `argsJsonSchema` — o schema exato vem sob demanda via `catalog_describe`.
export const CatalogIndexEntrySchema = z.object({
  name: z.string(),
  kind: z.enum(["display", "action", "navigate"]),
  minRole: UserRoleSchema,
  description: z.string(), // uma linha, distintiva
});
export type CatalogIndexEntry = z.infer<typeof CatalogIndexEntrySchema>;
export const CatalogIndexSchema = z.object({
  catalogVersion: z.string(),
  commands: z.array(CatalogIndexEntrySchema),
});
export type CatalogIndex = z.infer<typeof CatalogIndexSchema>;

// Claims do token MCP escopado (Canal 2). Worker assina (fast-jwt HS256, TTL ~3min), API verifica.
// Contrato interno worker↔api — mora aqui p/ ter forma única (não é Zod: fast-jwt não é dep leaf).
export const ASSISTANT_MCP_SCOPE = "mcp-read";
export interface AssistantMcpTokenClaims {
  orgId: string;
  userId: string;
  role: z.infer<typeof UserRoleSchema>;
  conversationId: string;
  turnId: string;
  scope: typeof ASSISTANT_MCP_SCOPE;
}

// ── Params de URL (domínio) ────────────────────────────────────────────────
export const TurnIdParamsSchema = z.object({ turnId: UuidSchema });
export const CommandIdParamsSchema = z.object({ commandId: UuidSchema });

// ── Enums espelhando o Prisma (wire) ───────────────────────────────────────
export const AssistantMessageRoleSchema = z.enum(["USER", "ASSISTANT"]);
export const AssistantTurnStatusSchema = z.enum([
  "QUEUED",
  "RUNNING",
  "COMPLETED",
  "FAILED",
  "CANCELED",
]);
export const AssistantCommandKindSchema = z.enum(["DISPLAY", "ACTION", "NAVIGATE"]);
export const AssistantCommandRiskTierSchema = z.enum(["SAFE", "CONFIRM", "DESTRUCTIVE"]);
export const AssistantCommandStatusSchema = z.enum([
  "PROPOSED",
  "AUTO_EXECUTED",
  "CONFIRMED",
  "EXECUTED",
  "REJECTED",
  "EXPIRED",
  "FAILED",
]);

// JSON de args/resultRef — objeto arbitrário (args já validados pelo CommandSpec na chegada).
const JsonRecordSchema = z.record(z.string(), z.unknown());

// ── AssistantCommandView — projeção do model (inclui expiresAt p/ countdown do card) ──
export const AssistantCommandViewSchema = z.object({
  id: UuidSchema,
  turnId: UuidSchema, // agrupa o card sob a mensagem do assistente que o produziu (thread)
  name: z.string(),
  kind: AssistantCommandKindSchema,
  riskTier: AssistantCommandRiskTierSchema,
  status: AssistantCommandStatusSchema,
  argsJson: JsonRecordSchema,
  seq: z.number().int(),
  expiresAt: z.string().datetime().nullable().optional(),
  resultRef: JsonRecordSchema.nullable().optional(),
  error: z.string().nullable().optional(),
  // Nome da entidade a digitar na confirmação reforçada (tier destrutivo).
  entityName: z.string().nullable().optional(),
});
export type AssistantCommandView = z.infer<typeof AssistantCommandViewSchema>;

// Corpo do confirm: `confirmationName` obrigatório-match p/ tier destrutivo
// (digitar o nome da entidade, como nas telas). Ausente/errado ⇒ 409 ASSISTANT_CONFIRMATION_MISMATCH.
export const ConfirmCommandRequestSchema = z
  .object({ confirmationName: z.string().min(1).max(200).optional() })
  .strict();
export type ConfirmCommandRequest = z.infer<typeof ConfirmCommandRequestSchema>;

// ── Status (sempre responde, mesmo com a feature desligada) ─────────────────
export const AssistantStatusResponseSchema = z.object({ enabled: z.boolean() });
export type AssistantStatusResponse = z.infer<typeof AssistantStatusResponseSchema>;

// ── Conversas ───────────────────────────────────────────────────────────────
export const ConversationSummarySchema = z.object({
  id: UuidSchema,
  title: z.string().nullable(),
  lastMessageAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  retentionExpiresAt: z.string().datetime(),
});
export type ConversationSummary = z.infer<typeof ConversationSummarySchema>;

export const AssistantMessageViewSchema = z.object({
  id: UuidSchema,
  role: AssistantMessageRoleSchema,
  content: z.string(),
  turnId: UuidSchema.nullable(),
  createdAt: z.string().datetime(),
});
export type AssistantMessageView = z.infer<typeof AssistantMessageViewSchema>;

export const ActiveTurnSchema = z.object({ id: UuidSchema, status: AssistantTurnStatusSchema });
export type ActiveTurn = z.infer<typeof ActiveTurnSchema>;

// Comando que a engine propôs mas a plataforma DESCARTOU na validação
// (allowlist/args Zod/minRole/cap). O dock avisa o usuário em vez de silêncio ("disse que fez e nada
// aconteceu"). Vai no evento SSE `done` (ao vivo) E na ConversationDetail (persistido ⇒ sobrevive F5).
export const DiscardedCommandReasonSchema = z.enum([
  "not_in_catalog", // nome fora do catálogo
  "invalid_args", // args não passam no Zod (ex.: period FREE sem periodStart/periodEnd)
  "forbidden_role", // exige GESTOR e o usuário é OPERADOR
  "cap_exceeded", // além do limite de comandos por turno
]);
export type DiscardedCommandReason = z.infer<typeof DiscardedCommandReasonSchema>;
export const DiscardedCommandSchema = z.object({
  name: z.string(),
  reason: DiscardedCommandReasonSchema,
});
export type DiscardedCommand = z.infer<typeof DiscardedCommandSchema>;
// Projeção persistida (por turno) — agrupada no dock por turnId, como os comandos.
export const DiscardedCommandViewSchema = DiscardedCommandSchema.extend({ turnId: UuidSchema });
export type DiscardedCommandView = z.infer<typeof DiscardedCommandViewSchema>;

export const ConversationDetailSchema = ConversationSummarySchema.extend({
  messages: z.array(AssistantMessageViewSchema),
  commands: z.array(AssistantCommandViewSchema),
  discarded: z.array(DiscardedCommandViewSchema).default([]),
  activeTurn: ActiveTurnSchema.nullable().optional(),
});
export type ConversationDetail = z.infer<typeof ConversationDetailSchema>;

export const CreateConversationRequestSchema = z.object({
  title: z.string().min(1).max(80).optional(),
});
export type CreateConversationRequest = z.infer<typeof CreateConversationRequestSchema>;

export const RenameConversationRequestSchema = z.object({ title: z.string().min(1).max(80) });
export type RenameConversationRequest = z.infer<typeof RenameConversationRequestSchema>;

export const DeleteConversationResponseSchema = z.object({ id: UuidSchema });

// ── Mensagem / turno ─────────────────────────────────────────────────────────
export const AssistantContextSchema = z.object({
  currentLineId: UuidSchema.optional(),
  screen: z.string().max(200).optional(),
});
export type AssistantContext = z.infer<typeof AssistantContextSchema>;

export const SendMessageRequestSchema = z.object({
  content: z.string().min(1).max(4000),
  context: AssistantContextSchema.optional(),
});
export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;

export const SendMessageResponseSchema = z.object({
  turnId: UuidSchema,
  messageId: UuidSchema,
  conversationId: UuidSchema,
});
export type SendMessageResponse = z.infer<typeof SendMessageResponseSchema>;

export const CancelTurnResponseSchema = z.object({
  id: UuidSchema,
  status: AssistantTurnStatusSchema,
});

export const ConfirmCommandResponseSchema = z.object({
  id: UuidSchema,
  status: AssistantCommandStatusSchema,
  resultRef: JsonRecordSchema.nullable().optional(),
  // Segredo efêmero (chave de API de device.create/rotateKey), exibido UMA vez
  // no card. NUNCA persistido: só existe nesta resposta viva; some no F5/replay idempotente.
  oneTimeSecret: z.string().optional(),
});
export type ConfirmCommandResponse = z.infer<typeof ConfirmCommandResponseSchema>;

export const RejectCommandResponseSchema = z.object({
  id: UuidSchema,
  status: AssistantCommandStatusSchema,
});

// ── SSE — evento do stream do turno (discriminated union) ────────────────────
export const AssistantStreamEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("status"), step: z.enum(["pensando", "gerando"]) }),
  z.object({ type: z.literal("tool"), step: z.string(), tool: z.string() }),
  z.object({ type: z.literal("command"), command: AssistantCommandViewSchema }),
  z.object({
    type: z.literal("done"),
    messageId: z.string(),
    text: z.string(),
    commands: z.array(AssistantCommandViewSchema),
    // Os descartados NÃO viajam aqui: o dock os lê da ConversationDetail (persistido ⇒ sobrevive F5).
  }),
  z.object({ type: z.literal("error"), message: z.string() }),
  z.object({ type: z.literal("canceled") }),
]);
export type AssistantStreamEvent = z.infer<typeof AssistantStreamEventSchema>;

// ── Engine (Canal 1: worker→n8n request; Canal 3: response no corpo) ─────────
// HMAC vai no header (X-Opus-Signature/Timestamp), NÃO no body.
export const EngineMessageSchema = z.object({
  role: z.enum(["user", "assistant"]),
  content: z.string(),
});

// Retorno de validação da re-consulta: o worker devolve à engine o comando recusado por
// obrigatório ausente para que ELA redija a pergunta. `.strict()` de propósito: campo novo aqui sem
// pensar quebra o teste em vez de vazar dado para fora do worker. APENAS nomes — nunca valores
// recebidos do usuário.
export const ValidationFeedbackSchema = z
  .object({
    // Comando que o agente tentou emitir e o worker recusou.
    commandName: z.string().min(1),
    // Caminhos dos obrigatórios ausentes. Nunca vazio: sem campo faltante não há re-consulta.
    missingFields: z.array(z.string().min(1)).min(1),
  })
  .strict();
export type ValidationFeedback = z.infer<typeof ValidationFeedbackSchema>;

export const AssistantTurnRequestSchema = z.object({
  protocolVersion: z.literal("1"),
  catalogVersion: z.string(),
  conversation: z.object({ id: UuidSchema }),
  user: z.object({ role: UserRoleSchema }), // SÓ o papel — zero PII
  context: AssistantContextSchema.optional(),
  // Índice fino filtrado por papel; opcional p/ retrocompat com o n8n vivo (D2).
  catalogIndex: CatalogIndexSchema.optional(),
  // Presente SÓ na re-consulta (aditivo, sem bump de protocolo — padrão do catalogIndex).
  validationFeedback: ValidationFeedbackSchema.optional(),
  messages: z.array(EngineMessageSchema), // janela: últimas 20 (asc)
  mcp: z.object({
    endpoint: z.string().url(),
    token: z.string(),
    expiresAt: z.string().datetime(),
  }),
});
export type AssistantTurnRequest = z.infer<typeof AssistantTurnRequestSchema>;

// Comando cru vindo da engine — validado 1-a-1 na chegada (worker) contra o CommandSpec.
export const EngineCommandSchema = z.object({
  name: z.string(),
  args: JsonRecordSchema.default({}),
});
export type EngineCommand = z.infer<typeof EngineCommandSchema>;

export const AssistantTurnResponseSchema = z.object({
  protocolVersion: z.literal("1"),
  assistantText: z.string(),
  commands: z.array(EngineCommandSchema).default([]),
  usage: z
    .object({ inputTokens: z.number().optional(), outputTokens: z.number().optional() })
    .optional(),
});
export type AssistantTurnResponse = z.infer<typeof AssistantTurnResponseSchema>;
